import { Injectable } from '@angular/core';
import { PDFDocument } from 'pdf-lib';

/**
 * Represents a document added in the CAT Compose "Document order" step.
 * Uploaded PDFs/images carry their bytes as a raw (non data-URI) base64
 * string; the template does not (it is rendered server-side).
 */
export interface ComposeDocument {
  type: 'pdf' | 'image' | 'template';
  base64?: string;
  order?: number;
  name?: string;
}

@Injectable({ providedIn: 'root' })
export class ComposePreviewService {
  /**
   * Builds the same combined document the signer will ultimately see, so the
   * "Assign Signatures" drag-drop preview shows the template AND every
   * uploaded PDF/image (not just the template).
   *
   * The template renders first (order 0, enforced by the compose screen),
   * followed by the uploaded documents in their arranged order — matching the
   * backend's merge order at placement time, so placed signature page indices
   * stay aligned.
   *
   * Returns a raw base64 string (no data-URI prefix), the format
   * app-pdf-virtual-viewer's normalizeInput() expects.
   */
  async buildCombinedPdf(
    templateBase64: string,
    documents: ComposeDocument[] = [],
  ): Promise<string> {
    const merged = await PDFDocument.create();

    // 1) Template pages first.
    await this.appendPdf(merged, this.stripDataUri(templateBase64));

    // 2) Uploaded documents in arranged order (template excluded — it is the
    //    resolved templateBase64 above).
    const extras = (documents || [])
      .filter((d) => d && d.type !== 'template' && d.base64)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    for (const doc of extras) {
      const base64 = this.stripDataUri(doc.base64 as string);
      if (doc.type === 'image') {
        await this.appendImage(merged, base64);
      } else {
        await this.appendPdf(merged, base64);
      }
    }

    const bytes = await merged.save();
    return this.bytesToBase64(bytes);
  }

  private async appendPdf(target: PDFDocument, base64: string): Promise<void> {
    try {
      const src = await PDFDocument.load(this.base64ToBytes(base64));
      const pages = await target.copyPages(src, src.getPageIndices());
      pages.forEach((p) => target.addPage(p));
    } catch {
      // A single unreadable document shouldn't blank the whole preview;
      // skip it and keep the rest of the combined document viewable.
    }
  }

  private async appendImage(target: PDFDocument, base64: string): Promise<void> {
    try {
      const bytes = this.base64ToBytes(base64);
      let img;
      try {
        img = await target.embedPng(bytes);
      } catch {
        img = await target.embedJpg(bytes);
      }
      const page = target.addPage([img.width, img.height]);
      page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
    } catch {
      // Skip an unreadable image rather than failing the whole preview.
    }
  }

  private stripDataUri(data: string): string {
    if (!data) return '';
    return (data.includes(',') ? data.split(',').pop()! : data).replace(/\s/g, '');
  }

  private base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }
}
